import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { api, useAuth } from '../App';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import { Plus, Search, Edit, Trash2, User, Phone, Mail, MapPin, Eye, Calendar, ArrowUpDown, ArrowDown, ArrowUp, Filter } from 'lucide-react';
import GuestProfile from '../components/GuestProfile';
import { formatCurrency } from '../utils/formatters';

export default function GuestsPage() {
  const { user } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const [guests, setGuests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [showDialog, setShowDialog] = useState(false);
  const [selectedGuest, setSelectedGuest] = useState(null);
  const [viewingGuest, setViewingGuest] = useState(null);
  
  // Tri et filtres
  const [sortBy, setSortBy] = useState('name'); // name, stays, spent, recent
  const [sortOrder, setSortOrder] = useState('asc');
  const [filterCountry, setFilterCountry] = useState('');
  const [filterVip, setFilterVip] = useState('all'); // all, vip, regular
  
  // Check if user can see financial info
  const canSeeFinancials = user?.permissions?.includes('finance.view') || 
                           user?.role?.toLowerCase() === 'propriétaire' ||
                           user?.role?.toLowerCase() === 'owner';
  
  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    email: '',
    phone: '',
    country: '',
    address: '',
    notes: ''
  });

  useEffect(() => {
    fetchGuests();
  }, [search]);

  // Handle URL parameter for direct navigation
  useEffect(() => {
    const guestId = searchParams.get('id');
    if (guestId && guests.length > 0) {
      const guest = guests.find(g => g.id === parseInt(guestId));
      if (guest) {
        setViewingGuest(guest);
        // Clear the URL parameter after opening
        setSearchParams({});
      }
    }
  }, [searchParams, guests]);

  const fetchGuests = async () => {
    try {
      const response = await api.get('/guests', { params: { search, limit: 100 } });
      setGuests(response.data.data);
    } catch (error) {
      toast.error('Erreur lors du chargement des clients');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (selectedGuest) {
        await api.put(`/guests/${selectedGuest.id}`, formData);
        toast.success('Client mis à jour');
      } else {
        await api.post('/guests', formData);
        toast.success('Client créé');
      }
      setShowDialog(false);
      resetForm();
      fetchGuests();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Erreur');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Supprimer ce client ?')) return;
    try {
      await api.delete(`/guests/${id}`);
      toast.success('Client supprimé');
      fetchGuests();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Erreur');
    }
  };

  const resetForm = () => {
    setFormData({
      first_name: '',
      last_name: '',
      email: '',
      phone: '',
      country: '',
      address: '',
      notes: ''
    });
    setSelectedGuest(null);
  };

  const openEditDialog = (guest) => {
    setSelectedGuest(guest);
    setFormData({
      first_name: guest.first_name,
      last_name: guest.last_name,
      email: guest.email || '',
      phone: guest.phone || '',
      country: guest.country || '',
      address: guest.address || '',
      notes: guest.notes || ''
    });
    setShowDialog(true);
  };

  // Obtenir les pays uniques
  const uniqueCountries = [...new Set(guests.filter(g => g.country).map(g => g.country))].sort();

  // Fonction de tri et filtrage
  const getSortedAndFilteredGuests = () => {
    let filtered = [...guests];
    
    // Filtre par pays
    if (filterCountry) {
      filtered = filtered.filter(g => g.country === filterCountry);
    }
    
    // Filtre VIP
    if (filterVip === 'vip') {
      filtered = filtered.filter(g => g.vip || g.total_stays >= 3);
    } else if (filterVip === 'regular') {
      filtered = filtered.filter(g => !g.vip && g.total_stays < 3);
    }
    
    // Tri
    filtered.sort((a, b) => {
      let comparison = 0;
      switch (sortBy) {
        case 'name':
          comparison = `${a.first_name} ${a.last_name}`.localeCompare(`${b.first_name} ${b.last_name}`);
          break;
        case 'stays':
          comparison = (b.total_stays || 0) - (a.total_stays || 0);
          break;
        case 'spent':
          comparison = (b.total_spent || 0) - (a.total_spent || 0);
          break;
        case 'recent':
          comparison = new Date(b.updatedAt || 0) - new Date(a.updatedAt || 0);
          break;
        default:
          comparison = 0;
      }
      return sortOrder === 'asc' ? comparison : -comparison;
    });
    
    return filtered;
  };

  const sortedGuests = getSortedAndFilteredGuests();

  // If viewing a guest profile, show the profile component
  if (viewingGuest) {
    return (
      <GuestProfile 
        guest={viewingGuest}
        onBack={() => {
          setViewingGuest(null);
          fetchGuests(); // Refresh list in case of updates
        }}
        onGuestUpdate={(updatedGuest) => {
          setViewingGuest(updatedGuest);
          fetchGuests();
        }}
      />
    );
  }

  return (
    <div className="space-y-6" data-testid="guests-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-semibold">Clients</h1>
          <p className="text-muted-foreground mt-1">Gestion des clients</p>
        </div>
        <Button onClick={() => { resetForm(); setShowDialog(true); }} data-testid="add-guest-btn">
          <Plus className="w-4 h-4 mr-2" />
          Nouveau client
        </Button>
      </div>

      {/* Search and Filters */}
      <Card>
        <CardContent className="p-4 space-y-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input
              placeholder="Rechercher par nom, email ou téléphone..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-10"
            />
          </div>
          
          {/* Filtres et tri */}
          <div className="flex flex-wrap gap-3 items-center">
            <div className="flex items-center gap-2">
              <Filter className="w-4 h-4 text-muted-foreground" />
              <span className="text-sm text-muted-foreground">Filtres:</span>
            </div>
            
            {/* Tri */}
            <Select value={sortBy} onValueChange={setSortBy}>
              <SelectTrigger className="w-[150px]" data-testid="sort-select">
                <ArrowUpDown className="w-3 h-3 mr-2" />
                <SelectValue placeholder="Trier par" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="name">Nom</SelectItem>
                <SelectItem value="stays">Nb séjours</SelectItem>
                <SelectItem value="spent">Total dépensé</SelectItem>
                <SelectItem value="recent">Plus récent</SelectItem>
              </SelectContent>
            </Select>
            
            <Button 
              variant="outline" 
              size="icon" 
              onClick={() => setSortOrder(prev => prev === 'asc' ? 'desc' : 'asc')}
              title={sortOrder === 'asc' ? 'Ordre croissant' : 'Ordre décroissant'}
            >
              {sortOrder === 'asc' ? <ArrowUp className="w-4 h-4" /> : <ArrowDown className="w-4 h-4" />}
            </Button>
            
            {/* Filtre pays */}
            {uniqueCountries.length > 0 && (
              <Select value={filterCountry || 'all'} onValueChange={(v) => setFilterCountry(v === 'all' ? '' : v)}>
                <SelectTrigger className="w-[130px]">
                  <SelectValue placeholder="Tous pays" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Tous pays</SelectItem>
                  {uniqueCountries.map(country => (
                    <SelectItem key={country} value={country}>{country}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
            
            {/* Filtre VIP */}
            <Select value={filterVip} onValueChange={setFilterVip}>
              <SelectTrigger className="w-[130px]">
                <SelectValue placeholder="Tous clients" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tous clients</SelectItem>
                <SelectItem value="vip">Fidèles (3+ séjours)</SelectItem>
                <SelectItem value="regular">Occasionnels</SelectItem>
              </SelectContent>
            </Select>
            
            {/* Compteur */}
            <Badge variant="outline" className="ml-auto">
              {sortedGuests.length} client{sortedGuests.length > 1 ? 's' : ''}
            </Badge>
          </div>
        </CardContent>
      </Card>

      {/* Guests Grid */}
      {loading ? (
        <div className="flex justify-center p-8">
          <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-primary"></div>
        </div>
      ) : sortedGuests.length === 0 ? (
        <Card>
          <CardContent className="text-center py-12 text-muted-foreground">
            Aucun client trouvé
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {sortedGuests.map((guest) => (
            <Card 
              key={guest.id} 
              className="card-metric hover:shadow-md transition-shadow cursor-pointer" 
              data-testid={`guest-card-${guest.id}`}
              onClick={() => setViewingGuest(guest)}
            >
              <CardContent className="p-4">
                <div className="flex items-start justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center">
                      <span className="text-primary font-semibold">
                        {guest.first_name[0]}{guest.last_name[0]}
                      </span>
                    </div>
                    <div>
                      <h3 className="font-semibold">{guest.first_name} {guest.last_name}</h3>
                      {guest.country && (
                        <Badge variant="outline" className="text-xs mt-1">
                          {guest.country}
                        </Badge>
                      )}
                    </div>
                  </div>
                  <div className="flex gap-1">
                    <Button variant="ghost" size="icon" onClick={(e) => { e.stopPropagation(); setViewingGuest(guest); }} title="Voir fiche">
                      <Eye className="w-4 h-4" />
                    </Button>
                    <Button variant="ghost" size="icon" onClick={(e) => { e.stopPropagation(); openEditDialog(guest); }} title="Modifier">
                      <Edit className="w-4 h-4" />
                    </Button>
                    <Button variant="ghost" size="icon" onClick={(e) => { e.stopPropagation(); handleDelete(guest.id); }} title="Supprimer">
                      <Trash2 className="w-4 h-4 text-red-500" />
                    </Button>
                  </div>
                </div>

                <div className="mt-4 space-y-2 text-sm">
                  {guest.email && (
                    <div className="flex items-center gap-2 text-muted-foreground">
                      <Mail className="w-4 h-4" />
                      <span className="truncate">{guest.email}</span>
                    </div>
                  )}
                  {guest.phone && (
                    <div className="flex items-center gap-2 text-muted-foreground">
                      <Phone className="w-4 h-4" />
                      <span>{guest.phone}</span>
                    </div>
                  )}
                </div>

                <div className="mt-4 pt-4 border-t flex justify-between text-sm">
                  <div>
                    <p className="text-muted-foreground">Séjours</p>
                    <p className="font-semibold">{guest.total_stays || 0}</p>
                  </div>
                  {canSeeFinancials && (
                    <div className="text-right">
                      <p className="text-muted-foreground">Total dépensé</p>
                      <p className="font-semibold text-green-600">{formatCurrency(guest.total_spent || 0)}</p>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Add/Edit Dialog */}
      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {selectedGuest ? 'Modifier le client' : 'Nouveau client'}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Prénom *</Label>
                <Input
                  value={formData.first_name}
                  onChange={(e) => setFormData({ ...formData, first_name: e.target.value })}
                  required
                />
              </div>
              <div>
                <Label>Nom *</Label>
                <Input
                  value={formData.last_name}
                  onChange={(e) => setFormData({ ...formData, last_name: e.target.value })}
                  required
                />
              </div>
              <div>
                <Label>Email</Label>
                <Input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                />
              </div>
              <div>
                <Label>Téléphone</Label>
                <Input
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                />
              </div>
              <div>
                <Label>Pays (code)</Label>
                <Input
                  value={formData.country}
                  onChange={(e) => setFormData({ ...formData, country: e.target.value.toUpperCase() })}
                  maxLength={2}
                  placeholder="FR, MA, GB..."
                />
              </div>
            </div>
            <div>
              <Label>Adresse</Label>
              <Input
                value={formData.address}
                onChange={(e) => setFormData({ ...formData, address: e.target.value })}
              />
            </div>
            <div>
              <Label>Notes</Label>
              <Textarea
                value={formData.notes}
                onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                rows={2}
              />
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowDialog(false)}>
                Annuler
              </Button>
              <Button type="submit">
                {selectedGuest ? 'Mettre à jour' : 'Créer'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
